import React from 'react';
import classNames from 'classnames';

import { Reel } from 'types/reelPlayer';

import ActiveReelBar from './ActiveReelBar';

import styles from './ReelBars.module.scss';

const ReelBars = React.memo<{
  isFullScreen: boolean;
  reels: Reel[];
  currentReelIndex: number;
  handleReelBarClick: (index: number, isPaywalled: boolean) => void;
}>(({ isFullScreen, reels, currentReelIndex, handleReelBarClick }) => {
  return (
    <div className={styles.wrapper}>
      {reels.map((reel: Reel, index: number) => {
        const isLastBar = index === reels.length - 1;
        const paywallUrl = 'paywalled-highlight-reel';
        const isPaywalled = reel.highlightUrl === paywallUrl;
        const isActiveReel = index === currentReelIndex && !isPaywalled;

        const surroundingReelsAreFromToday =
          reels[index + 1]?.isFromToday && reels[index - 1]?.isFromToday;
        const surroundingReelsAreNotPaywalled =
          reels[index + 1]?.highlightUrl !== paywallUrl &&
          reels[index - 1]?.highlightUrl !== paywallUrl;

        const shouldShowAdIndicator =
          (index + 1) % 2 === 0 &&
          !isLastBar &&
          surroundingReelsAreFromToday &&
          surroundingReelsAreNotPaywalled;

        return (
          <React.Fragment key={`fragment-${index + 1}`}>
            {isActiveReel ? (
              // Key on highlightUrl so a clip change (e.g. cam switch at same index) remounts with fresh progress.
              <ActiveReelBar
                key={`active-${reel.highlightUrl ?? index}`}
                reel={reel}
                isFullScreen={isFullScreen}
              />
            ) : (
              <div
                key={`bar-${index + 1}`}
                className={classNames(
                  styles.bar,
                  currentReelIndex > index && styles.watchedBar,
                  isFullScreen && styles.fullScreenBar,
                  isPaywalled && styles.paywalledBar,
                )}
                onClick={() => handleReelBarClick(index, isPaywalled)}
              />
            )}
            {shouldShowAdIndicator && (
              <div
                key={`ad-indicator-${index + 1}`}
                className={classNames(
                  styles.adIndicator,
                  isFullScreen && styles.fullScreenAdIndicator,
                )}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
});

ReelBars.displayName = 'ReelBars';

export default ReelBars;
